/** Asistente de la Pizarra: configura el elemento seleccionado con preguntas cortas,
 *  busca en el Catálogo Soluarq real o propone un producto no encontrado (bandeja de aprobación). */
import { useEffect, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ICONO_TIPO } from '@/components/pizarra/LienzoPizarra';

const Chips = ({ opciones, valor, onPick, testid }) => (
  <div className="flex flex-wrap gap-1.5">
    {opciones.map((o) => (
      <button key={o} type="button" onClick={() => onPick(o)}
        className={`text-[11px] font-bold px-2.5 py-1.5 rounded-full border transition-colors ${valor === o ? 'bg-[#7352B5] text-white border-[#7352B5]' : 'bg-white text-[#38205F] border-[#B9A5D9] hover:bg-[#F4EFFB]'}`}
        data-testid={`${testid}-${o}`}>{o}</button>
    ))}
  </div>
);

const Campo = ({ label, children }) => (
  <div className="space-y-1">
    <p className="text-[10px] font-black uppercase text-[#7352B5]">{label}</p>
    {children}
  </div>
);

const TIPOS = ['vidrio', 'puerta', 'bisagra', 'clip', 'jalador', 'perfil', 'cerradura', 'union', 'otro'];

export const ConfigElemento = ({ elemento, pizarraId, mediciones = [], onPatch, onEliminar, onDuplicar, onCerrar }) => {
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState(null);
  const [propio, setPropio] = useState(null);
  const [sugerencias, setSugerencias] = useState(null);
  const el = elemento;
  const p = el.props || {};
  const patch = (np) => onPatch(el.id, { props: { ...p, ...np } });
  const esHerraje = !['vidrio', 'puerta', 'union'].includes(el.tipo);

  useEffect(() => { setQ(''); setResultados(null); setPropio(null); }, [el.id]);

  // Sugerencia IA: mejor coincidencia del catálogo para el herraje leído del dibujo
  useEffect(() => {
    setSugerencias(null);
    if (!esHerraje || el.producto) return;
    const texto = p.descripcion || '';
    api.get(`/sa/pizarra/sugerir?texto=${encodeURIComponent(texto)}&tipo=${encodeURIComponent(el.tipo === 'otro' ? '' : el.tipo)}`)
      .then((r) => setSugerencias(r.data.sugerencias || []))
      .catch(() => setSugerencias([]));
  }, [el.id, el.tipo, !!el.producto]); // eslint-disable-line

  const aceptarSugerencia = (s) => {
    onPatch(el.id, {
      producto: { codigo: s.codigo, nombre: s.nombre, unidad: s.unidad, acabado: s.acabado, precio: s.precio },
      estado: s.pendiente ? 'incompleto' : 'confirmado',
    });
    toast.success(s.pendiente ? `${s.nombre} vinculado — precio pendiente de autorización` : `${s.nombre} vinculado ✅`);
  };

  const buscar = async () => {
    try {
      const { data } = await api.get(`/sa/catalogo?q=${encodeURIComponent(q)}`);
      setResultados((data.items || data || []).slice(0, 8));
    } catch { toast.error('Error buscando en catálogo'); }
  };

  const elegirProducto = async (item) => {
    let precio = 0, pendiente = true;
    try {
      const { data } = await api.post('/sa/pizarra/precio-item', { costo: Number(item.costo || 0) });
      precio = data.precio; pendiente = data.pendiente;
    } catch { /* precio pendiente */ }
    onPatch(el.id, {
      producto: { codigo: item.codigo, nombre: item.nombre, unidad: item.unidad || 'unidad', acabado: item.acabado || '', precio },
      estado: pendiente ? 'incompleto' : 'confirmado',
    });
    setResultados(null);
    toast.success(pendiente ? `${item.nombre} vinculado — precio pendiente de autorización` : `${item.nombre} vinculado ✅`);
  };

  const proponer = async () => {
    if (!propio?.descripcion?.trim()) return toast.error('Escribe la descripción del producto');
    try {
      await api.post('/sa/pizarra/productos-propuestos', { ...propio, pizarra_id: pizarraId });
      onPatch(el.id, {
        producto: { codigo: propio.codigo || 'PROPUESTO', nombre: propio.descripcion, unidad: 'unidad', acabado: propio.color || '', precio: 0, propuesto: true },
        estado: 'incompleto',
      });
      setPropio(null);
      toast.success('Producto enviado a la bandeja de aprobación — precio pendiente de autorización');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al proponer'); }
  };

  return (
    <div className="space-y-3" data-testid="pizarra-config-elemento">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-black text-[#1F1B29]">{ICONO_TIPO[el.tipo] || '📦'} Marcador {el.etiqueta}</p>
        <div className="flex gap-1">
          <button onClick={() => onDuplicar(el.id)} className="text-[10px] font-bold px-2 py-1 rounded border border-[#B9A5D9] text-[#38205F]" data-testid="pizarra-el-duplicar">Duplicar</button>
          <button onClick={() => onEliminar(el.id)} className="text-[10px] font-bold px-2 py-1 rounded border border-red-300 text-red-500" data-testid="pizarra-el-eliminar">Eliminar</button>
          <button onClick={onCerrar} className="text-[10px] font-bold px-2 py-1 rounded border border-[#B9A5D9] text-[#1F1B29]" data-testid="pizarra-el-cerrar">✕</button>
        </div>
      </div>

      <Campo label="¿Qué elemento es?">
        <Chips opciones={TIPOS} valor={el.tipo} onPick={(t) => onPatch(el.id, { tipo: t })} testid="pizarra-tipo" />
      </Campo>

      {(el.tipo === 'vidrio' || el.tipo === 'puerta') && (
        <>
          <Campo label="Medidas reales (confírmalas — no se asumen del dibujo)">
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" placeholder="Ancho (m)" value={p.ancho_m ?? ''} onChange={(e) => patch({ ancho_m: e.target.value })} className="h-9 text-xs" data-testid="pizarra-el-ancho" />
              <Input type="number" placeholder="Alto (m)" value={p.alto_m ?? ''} onChange={(e) => patch({ alto_m: e.target.value })} className="h-9 text-xs" data-testid="pizarra-el-alto" />
            </div>
            {mediciones.filter((m) => m.estimada).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1" data-testid="pizarra-usar-medidas">
                {mediciones.filter((m) => m.estimada).slice(-4).map((m, i) => (
                  <span key={i} className="flex items-center gap-1 text-[9px] font-bold bg-amber-50 border border-amber-300 text-amber-700 rounded-full px-2 py-1">
                    📐 ~{m.m} m →
                    <button type="button" onClick={() => patch({ ancho_m: m.m })} className="underline font-black" data-testid={`usar-med-ancho-${i}`}>ancho</button>
                    <button type="button" onClick={() => patch({ alto_m: m.m })} className="underline font-black" data-testid={`usar-med-alto-${i}`}>alto</button>
                  </span>
                ))}
                <span className="text-[8px] text-[#6B6B6B] self-center">estimación editable — confírmala tú</span>
              </div>
            )}
          </Campo>
          <Campo label="Vidrio"><div className="grid grid-cols-3 gap-2">
            <Input placeholder="Tipo (templado…)" value={p.tipo_vidrio ?? ''} onChange={(e) => patch({ tipo_vidrio: e.target.value })} className="h-9 text-xs" data-testid="pizarra-el-tipovidrio" />
            <Input placeholder="Espesor" value={p.espesor ?? ''} onChange={(e) => patch({ espesor: e.target.value })} className="h-9 text-xs" data-testid="pizarra-el-espesor" />
            <Input placeholder="Color" value={p.color ?? ''} onChange={(e) => patch({ color: e.target.value })} className="h-9 text-xs" data-testid="pizarra-el-color" />
          </div></Campo>
          {el.tipo === 'puerta' && (
            <Campo label="¿Hacia dónde abre la puerta?">
              <Chips opciones={['izquierda', 'derecha']} valor={p.apertura} onPick={(v) => patch({ apertura: v })} testid="pizarra-apertura" />
            </Campo>
          )}
          <Button size="sm" className="w-full bg-[#10B981] hover:bg-[#0ea371] text-white font-bold"
            onClick={() => onPatch(el.id, { estado: (p.ancho_m && p.alto_m) ? 'confirmado' : 'incompleto' })}
            data-testid="pizarra-el-confirmar">
            {(p.ancho_m && p.alto_m) ? '✓ Confirmar elemento' : 'Guardar (faltan medidas → queda ámbar)'}
          </Button>
        </>
      )}

      {el.tipo === 'union' && (
        <>
          <Campo label="Ángulo de la unión (según geometría real)">
            <Chips opciones={['90°', '135°', '180°', 'Otro']} valor={p.angulo} onPick={(v) => patch({ angulo: v })} testid="pizarra-angulo" />
          </Campo>
          <Button size="sm" className="w-full bg-[#10B981] text-white font-bold" onClick={() => onPatch(el.id, { estado: p.angulo ? 'confirmado' : 'incompleto' })} data-testid="pizarra-union-confirmar">✓ Confirmar unión</Button>
        </>
      )}

      {!['vidrio', 'puerta', 'union'].includes(el.tipo) && (
        <>
          <Campo label="Cantidad">
            <Input type="number" min="1" value={p.cantidad ?? 1} onChange={(e) => patch({ cantidad: e.target.value })} className="h-9 text-xs w-24" data-testid="pizarra-el-cantidad" />
          </Campo>
          <Campo label="Producto del Catálogo Soluarq">
            {el.producto ? (
              <div className="text-xs bg-[#B9A5D9]/30 rounded-lg p-2 flex items-center justify-between gap-2" data-testid="pizarra-el-producto">
                <span><b>{el.producto.nombre}</b> · {el.producto.codigo}{el.producto.propuesto ? ' · ⏳ propuesto' : ''}{el.producto.precio ? ` · Q${el.producto.precio}` : ' · ⚠ precio pendiente'}</span>
                <button onClick={() => onPatch(el.id, { producto: null, estado: 'pendiente' })} className="text-red-500 font-black" data-testid="pizarra-el-quitar-producto">✕</button>
              </div>
            ) : (
              <>
                {sugerencias === null && <p className="text-[10px] text-[#6B6B6B]">🤖 Buscando coincidencias en el catálogo…</p>}
                {sugerencias?.length > 0 && (
                  <div className="border border-[#7352B5]/40 bg-[#F4EFFB] rounded-lg p-2 space-y-1.5 mb-1" data-testid="pizarra-sugerencia-ia">
                    <p className="text-[9px] font-black uppercase text-[#38205F]">🤖 Sugerencia del catálogo (según lo leído: "{(p.descripcion || el.tipo).slice(0, 40)}")</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] text-[#1F1B29] min-w-0">
                        <b>{sugerencias[0].nombre}</b> · {sugerencias[0].codigo}{sugerencias[0].acabado ? ` · ${sugerencias[0].acabado}` : ''}
                        {sugerencias[0].pendiente ? ' · ⚠ precio pendiente' : ` · Q${sugerencias[0].precio}`}
                      </span>
                      <button type="button" onClick={() => aceptarSugerencia(sugerencias[0])}
                        className="shrink-0 text-[10px] font-black px-2.5 py-1.5 rounded-lg bg-[#10B981] text-white"
                        data-testid="pizarra-aceptar-sugerencia">✓ Aceptar</button>
                    </div>
                    {sugerencias.slice(1).map((s) => (
                      <button key={s.codigo} type="button" onClick={() => aceptarSugerencia(s)}
                        className="block w-full text-left text-[10px] text-[#38205F] underline"
                        data-testid={`pizarra-sugerencia-alt-${s.codigo}`}>
                        o {s.nombre} · {s.codigo}{s.pendiente ? '' : ` · Q${s.precio}`}
                      </button>
                    ))}
                  </div>
                )}
                {sugerencias?.length === 0 && (
                  <p className="text-[10px] text-[#6B6B6B] mb-1">🤖 Sin coincidencia clara en el catálogo — búscalo o proponlo abajo.</p>
                )}
                <div className="flex gap-1.5">
                  <Input placeholder="Buscar por código, nombre, material…" value={q} onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && buscar()} className="h-9 text-xs" data-testid="pizarra-buscar-input" />
                  <Button size="sm" onClick={buscar} className="bg-[#7352B5] text-white font-bold" data-testid="pizarra-buscar-btn">Buscar</Button>
                </div>
                {resultados && (
                  <div className="border border-[#B9A5D9] rounded-lg max-h-40 overflow-y-auto mt-1" data-testid="pizarra-buscar-resultados">
                    {resultados.length === 0 && <p className="text-[11px] p-2 text-[#6B6B6B]">Sin coincidencias en el catálogo.</p>}
                    {resultados.map((it) => (
                      <button key={it.id || it.codigo} onClick={() => elegirProducto(it)}
                        className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-[#F4EFFB] border-b border-[#F4EFFB] last:border-0"
                        data-testid={`pizarra-resultado-${it.codigo}`}>
                        <b>{it.nombre}</b> · {it.codigo}{it.acabado ? ` · ${it.acabado}` : ''}
                      </button>
                    ))}
                  </div>
                )}
                {!propio ? (
                  <button onClick={() => setPropio({ codigo: '', descripcion: '', tipo_material: '', color: '', precio_referencial: 0, especificaciones: '', observaciones: '' })}
                    className="text-[11px] font-bold text-[#7352B5] underline mt-1" data-testid="pizarra-proponer-btn">➕ Agregar producto no encontrado</button>
                ) : (
                  <div className="border border-[#7352B5]/30 rounded-lg p-2 space-y-1.5 mt-1" data-testid="pizarra-proponer-form">
                    <p className="text-[10px] font-black text-[#7352B5] uppercase">Producto no encontrado → bandeja de aprobación</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      <Input placeholder="Código (si existe)" value={propio.codigo} onChange={(e) => setPropio({ ...propio, codigo: e.target.value })} className="h-8 text-xs" data-testid="prop-codigo" />
                      <Input placeholder="Material" value={propio.tipo_material} onChange={(e) => setPropio({ ...propio, tipo_material: e.target.value })} className="h-8 text-xs" data-testid="prop-material" />
                    </div>
                    <Input placeholder="Descripción *" value={propio.descripcion} onChange={(e) => setPropio({ ...propio, descripcion: e.target.value })} className="h-8 text-xs" data-testid="prop-descripcion" />
                    <div className="grid grid-cols-2 gap-1.5">
                      <Input placeholder="Color / acabado" value={propio.color} onChange={(e) => setPropio({ ...propio, color: e.target.value })} className="h-8 text-xs" data-testid="prop-color" />
                      <Input type="number" placeholder="Precio referencial" value={propio.precio_referencial || ''} onChange={(e) => setPropio({ ...propio, precio_referencial: Number(e.target.value) })} className="h-8 text-xs" data-testid="prop-precio" />
                    </div>
                    <Input placeholder="Observaciones" value={propio.observaciones} onChange={(e) => setPropio({ ...propio, observaciones: e.target.value })} className="h-8 text-xs" data-testid="prop-obs" />
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => setPropio(null)}>Cancelar</Button>
                      <Button size="sm" className="flex-1 bg-[#7352B5] text-white text-xs font-bold" onClick={proponer} data-testid="prop-enviar">Enviar a aprobación</Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Campo>
        </>
      )}

      <Campo label="Notas">
        <Input placeholder="Observaciones del elemento" value={p.notas ?? ''} onChange={(e) => patch({ notas: e.target.value })} className="h-9 text-xs" data-testid="pizarra-el-notas" />
      </Campo>
    </div>
  );
};
